import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface HostlyUser {
  id: string;
  name: string;
  lastName?: string;
  email: string;
  profilePictureUrl?: string;
  username?: string;
}

/**
 * Busca usuários do Hostly por nome, email ou username
 */
export const searchHostlyUsers = async (searchTerm: string): Promise<HostlyUser[]> => {
  try {
    const usersRef = collection(db, 'users');
    const searchLower = searchTerm.toLowerCase().trim();

    // Remove @ se foi digitado
    const cleanSearch = searchLower.startsWith('@')
      ? searchLower.substring(1)
      : searchLower;

    const results: HostlyUser[] = [];
    const seenIds = new Set<string>();

    // Busca 1: Por username (se começar com @)
    if (searchTerm.startsWith('@')) {
      const usernameQuery = query(
        usersRef,
        where('username', '>=', cleanSearch),
        where('username', '<=', cleanSearch + '\uf8ff'),
        limit(10)
      );
      const usernameSnapshot = await getDocs(usernameQuery);
      usernameSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!seenIds.has(doc.id)) {
          results.push({
            id: doc.id,
            name: data.name || '',
            lastName: data.lastName || '',
            email: data.email || '',
            profilePictureUrl: data.profilePictureUrl,
            username: data.username,
          });
          seenIds.add(doc.id);
        }
      });
    }

    // Busca por nome - Firestore é case-sensitive, então buscamos com variações
    const capitalizedSearch = cleanSearch.charAt(0).toUpperCase() + cleanSearch.slice(1);
    const nameVariants = [cleanSearch, capitalizedSearch];
    // Se o termo original do usuario tinha casing diferente, incluir também
    const originalTrimmed = searchTerm.trim();
    if (!nameVariants.includes(originalTrimmed) && !originalTrimmed.startsWith('@')) {
      nameVariants.push(originalTrimmed);
    }

    for (const variant of nameVariants) {
      const nameQuery = query(
        usersRef,
        where('name', '>=', variant),
        where('name', '<=', variant + '\uf8ff'),
        limit(10)
      );
      const nameSnapshot = await getDocs(nameQuery);
      nameSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!seenIds.has(doc.id)) {
          results.push({
            id: doc.id,
            name: data.name || '',
            lastName: data.lastName || '',
            email: data.email || '',
            profilePictureUrl: data.profilePictureUrl,
            username: data.username,
          });
          seenIds.add(doc.id);
        }
      });
    }

    const emailQuery = query(
      usersRef,
      where('email', '>=', cleanSearch),
      where('email', '<=', cleanSearch + '\uf8ff'),
      limit(10)
    );
    const emailSnapshot = await getDocs(emailQuery);
    emailSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (!seenIds.has(doc.id)) {
        results.push({
          id: doc.id,
          name: data.name || '',
          lastName: data.lastName || '',
          email: data.email || '',
          profilePictureUrl: data.profilePictureUrl,
          username: data.username,
        });
        seenIds.add(doc.id);
      }
    });

    // Ordena por relevância (username primeiro, depois nome, depois email)
    return results
      .sort((a, b) => {
        // Prioriza matches exatos em username
        if (a.username === cleanSearch) return -1;
        if (b.username === cleanSearch) return 1;

        // Depois matches que começam com o termo
        const aNameMatch = a.name.toLowerCase().startsWith(cleanSearch);
        const bNameMatch = b.name.toLowerCase().startsWith(cleanSearch);
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;

        // Por último, ordem alfabética
        return a.name.localeCompare(b.name);
      })
      .slice(0, 15); // Limita a 15 resultados no total
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    throw error;
  }
};

/**
 * Busca usuário por username exato
 */
export const getUserByUsername = async (username: string): Promise<HostlyUser | null> => {
  try {
    const usersRef = collection(db, 'users');
    const cleanUsername = username.toLowerCase().replace('@', '');

    const q = query(
      usersRef,
      where('username', '==', cleanUsername),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      name: data.name || '',
      lastName: data.lastName || '',
      email: data.email || '',
      profilePictureUrl: data.profilePictureUrl,
      username: data.username,
    };
  } catch (error) {
    console.error('Erro ao buscar usuário por username:', error);
    return null;
  }
};

/**
 * Verifica se um username está disponível
 */
export const isUsernameAvailable = async (username: string): Promise<boolean> => {
  try {
    const user = await getUserByUsername(username);
    return user === null;
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de username:', error);
    return false;
  }
};
